import React from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { AddressLink, CopyBtn } from "@/components/primitives";
import { AdaAmount } from "@/components/tx/AdaAmount";
import { _distinctAddrCount, _sumLovelace } from "@/components/tx/txHelpers";
import { _decodeAssetName, formatLovelace, truncMid } from "@/lib/format";

// ---------- Input / Output Flow Diagram (tx flow tab) ----------
export const FlowSideCard = ({ title, totalLovelace, rows, side }) => {
  const distinct = _distinctAddrCount(rows);
  return (
    <div
      data-testid={`flow-${side}-card`}
      className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card/40 backdrop-blur-md p-5 md:p-6"
    >
      <div className="text-center text-[12px] uppercase tracking-[0.3em] text-muted-foreground mono mb-4">
        {title}
      </div>
      <div className="text-center space-y-1.5 pb-5 mb-5 border-b border-primary/15">
        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
          {side === "in" ? "Total Input" : "Total Output"}
        </div>
        <div className="flex justify-center">
          <AdaAmount lovelaceStr={totalLovelace.toString()} />
        </div>
        <div className="text-[11px] text-muted-foreground/70 mono">
          ({distinct} {distinct === 1 ? "address" : "addresses"})
        </div>
      </div>
      <div className="space-y-3">
        {(rows || []).map((r, i) => {
          const ada = r.amount?.find?.((a) => a.unit === "lovelace")?.quantity || "0";
          const tokens = (r.amount || []).filter((a) => a.unit !== "lovelace");
          return (
            <div
              key={`flow-${side}-${i}`}
              data-testid={`flow-${side}-item-${i}`}
              className="rounded-xl border border-primary/15 bg-primary/[0.03] px-4 py-3 hover:border-[#22d3ee]/50 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <CopyBtn value={r.address} label="address" />
                <AddressLink value={r.address} display={truncMid(r.address, 14, 8)} className="font-mono text-[12px] text-[#22d3ee] break-all" />
              </div>
              {side === "in" && r.tx_hash ? (
                <div className="flex items-center gap-2 mt-1 pl-1 min-w-0">
                  <CopyBtn value={r.tx_hash} label="src-tx" />
                  <span className="font-mono text-[11px] text-muted-foreground/80 break-all">
                    {truncMid(r.tx_hash, 12, 6)}
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                    #{r.output_index ?? 0}
                  </span>
                </div>
              ) : null}
              <div className="mt-2 text-right">
                <AdaAmount lovelaceStr={ada} />
              </div>
              {tokens.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5 justify-end">
                  {tokens.map((t) => {
                    const { name } = _decodeAssetName(t.unit);
                    return (
                      <span
                        key={t.unit}
                        title={t.unit}
                        data-testid={`flow-${side}-token-${i}`}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-primary/25 bg-primary/[0.04] mono text-[11px] tabnum"
                      >
                        <span className="text-primary/90">{name || truncMid(t.unit, 6, 4)}</span>
                        <span className="text-foreground/90 font-semibold">
                          {Number(t.quantity).toLocaleString("en-US")}
                        </span>
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
        {(!rows || rows.length === 0) ? (
          <div className="px-1 py-4 text-[12px] mono text-muted-foreground/60">none</div>
        ) : null}
      </div>
    </div>
  );
};

export const TxFlowDiagram = ({ inputs, outputs }) => {
  const totalIn = _sumLovelace(inputs);
  const totalOut = _sumLovelace(outputs);
  const fees = totalIn > totalOut ? totalIn - totalOut : 0n;
  return (
    <div data-testid="tx-flow-diagram" className="space-y-6">
      <h3 className="font-heading text-[16px] md:text-[18px] text-[#22d3ee] text-glow-cyan">
        Input / Output Flow Diagram
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-5 items-start">
        <FlowSideCard title="Inputs" totalLovelace={totalIn} rows={inputs} side="in" />

        {/* Center — fees node */}
        <div className="flex lg:flex-col items-center justify-center gap-3 lg:pt-28">
          <div className="hidden lg:block h-[1px] w-10 bg-gradient-to-r from-transparent to-primary/60" />
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-[0_0_28px_rgba(157,92,255,0.55)]">
            <ArrowRight size={26} weight="bold" className="text-white" />
          </div>
          <div className="hidden lg:block h-[1px] w-10 bg-gradient-to-r from-primary/60 to-transparent" />
          <div className="text-center lg:mt-2" data-testid="flow-fees">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono">Fees</div>
            <div className="font-mono text-[15px] tabnum text-orange-400 whitespace-nowrap">
              {formatLovelace(fees.toString(), "ada")}
            </div>
          </div>
        </div>

        <FlowSideCard title="Outputs" totalLovelace={totalOut} rows={outputs} side="out" />
      </div>
    </div>
  );
};
